import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchApi,
  makeResetCache,
  notifyError,
  notifyResponse,
  PageTitle,
  ParallelLoad,
  Section,
  selectUser,
  useCacheRecord,
} from 'gossamer-front-end';
import { useNavigate, useParams } from 'react-router-dom';
import { Task, TaskDefinition, TaskDefinitionRow, TaskRow } from 'gossamer-model';
import { Button, Modal } from 'react-bootstrap';
import { DisplayMarkdown } from 'shared-front-end';

export const CheckTask = (): React.JSX.Element => {
  const params = useParams();
  const taskRecord = useCacheRecord(Task, params.key);
  const definitionRecord = useCacheRecord(
    TaskDefinition,
    taskRecord?.data?.definition_id,
    !taskRecord?.data?.definition_id,
  );

  return (
    <>
      <PageTitle title={'Task: ' + (taskRecord?.data?.label || '')} />
      {!!taskRecord.data && (
        <ReportAnIssue taskName={taskRecord.data.label} institutionId={taskRecord.data.institution_id} />
      )}
      <ParallelLoad responses={[taskRecord, definitionRecord]}>
        <CheckTaskInner
          taskRecord={taskRecord.data as TaskRow}
          definitionRecord={definitionRecord.data as TaskDefinitionRow}
        />
      </ParallelLoad>
    </>
  );
};

const CheckTaskInner = (props: { taskRecord: TaskRow; definitionRecord: TaskDefinitionRow }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const resetCache = makeResetCache(dispatch);
  const isProcessing = React.useRef(false);
  const handleClick = async () => {
    try {
      isProcessing.current = true;
      const resp = await fetchApi('POST', `/main/check-task-done/${props.taskRecord.id}`, {});
      resetCache(Task.id);
      notifyResponse(resp);
      navigate('/website/tasks');
    } catch (e) {
      notifyError(e, 'An error occurred while checking the task');
    } finally {
      isProcessing.current = false;
    }
  };

  return (
    <>
      <Section>
        <DisplayMarkdown source={props.definitionRecord.markdown_text} />
      </Section>
      <div>
        <Button onClick={handleClick} disabled={isProcessing.current}>
          Click to Confirm
        </Button>
      </div>
    </>
  );
};

interface ReportAnIssueProps {
  institutionId: number;
  taskName: string;
}

const ReportAnIssue = (props: ReportAnIssueProps) => {
  const user = useSelector(selectUser);
  const [show, setShow] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const handleSubmit = async (message: string) => {
    if (!message) {
      return;
    }
    try {
      const response = await fetchApi('POST', '/main/start-convo', {
        subject: `Issue with Task: ${props.taskName}`,
        message,
        toUserIds: [],
        institutionId: props.institutionId,
        forumType: 'User',
        forumKey: user.userId,
      });
      notifyResponse(response, 'Issue reported successfully');
      setShow(false);
    } catch (e) {
      notifyError(e, 'Failed to report the issue');
    }
  };
  return (
    <>
      <div style={{ width: '100%', textAlign: 'right', marginBottom: '1rem' }}>
        <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => setShow(true)}>
          Report an issue
        </span>
      </div>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        aria-labelledby="ref-issue-text-modal-title"
        size="lg"
        fullscreen="lg-down"
      >
        <Modal.Header closeButton>
          <Modal.Title id="ref-issue-text-modal-title">Report an Issue with this Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <textarea
            name="issueText"
            rows={5}
            ref={textareaRef}
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px' }}
            placeholder="Describe the issue you're facing with this task..."
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: '0.5rem' }}>
            <Button variant="secondary" size="sm" onClick={() => setShow(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={() => handleSubmit(textareaRef.current?.value || '')}>
              Submit Issue
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
